import React, { useState } from 'react';
import { Outlet, NavLink, useLocation, Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useAuth } from '../context/AuthContext';
import { petAPI } from '../services/api';
import {
  HomeIcon,
  HeartIcon,
  AcademicCapIcon,
  ChatBubbleLeftRightIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

const navigation = [
  { name: '🏠 Dashboard', href: '/app/dashboard', icon: HomeIcon },
  { name: '🐾 My Pets', href: '/app/pets', icon: HeartIcon },
  { name: '🎯 Exercises', href: '/app/exercises', icon: AcademicCapIcon },
  // { name: '📚 Programs', href: '/app/programs', icon: BookOpenIcon },
  { name: '🩺 Virtual Vet', href: '/app/virtual-vet', icon: ChatBubbleLeftRightIcon },
];

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const { data: pets } = useQuery('pets', petAPI.getPets);

  return (
    <div className="flex h-screen bg-gradient-to-br from-primary-50 via-secondary-50 to-accent-50">
      {/* Mobile sidebar */}
      <div className={`lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 z-40 flex">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
          <div className="relative flex flex-col flex-1 w-64 max-w-xs bg-gradient-to-b from-primary-600 to-secondary-600 shadow-2xl">
            <div className="absolute top-0 right-0 p-1 -mr-12">
              <button
                className="flex items-center justify-center w-10 h-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                onClick={() => setSidebarOpen(false)}
              >
                <XMarkIcon className="w-6 h-6 text-white" />
              </button>
            </div>
            <SidebarContent />
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:flex lg:flex-shrink-0">
        <div className="flex flex-col w-64 bg-gradient-to-b from-primary-600 to-secondary-600 shadow-2xl">
          <SidebarContent />
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col flex-1 overflow-hidden">
        {/* Top bar */}
        <div className="flex items-center justify-between px-4 py-4 bg-white/80 backdrop-blur-sm border-b border-primary-200 lg:px-6 shadow-sm">
          <button
            className="lg:hidden p-2 rounded-full hover:bg-primary-100 transition-colors"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="w-6 h-6 text-primary-600" />
          </button>
          
          <div className="hidden lg:block"></div>
          
          <div className="flex items-center space-x-4 ml-auto">
            <div className="flex items-center space-x-2">
              <SparklesIcon className="w-5 h-5 text-playful-blue animate-pulse" />
              <span className="text-lg font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
                Welcome back, {user?.firstName}! 🎉
              </span>
            </div>
            <button
              onClick={logout}
              className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-600 hover:text-white hover:bg-gradient-to-r hover:from-primary-500 hover:to-secondary-500 rounded-full transition-all duration-200"
            >
              <ArrowRightOnRectangleIcon className="w-4 h-4" />
              <span>Logout</span>
            </button>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto bg-gradient-to-br from-primary-50 via-secondary-50 to-accent-50">
          <Outlet />
        </main>
      </div>
    </div>
  );

  function SidebarContent() {
    return (
      <div className="flex flex-col flex-1 min-h-0">
        <div className="flex items-center justify-center h-20 px-4 bg-white/10 backdrop-blur-sm">
          <Link to="/app/exercises" className="flex items-center space-x-2 hover:opacity-80 transition-opacity">
            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center animate-bounce-slow">
              <span className="text-2xl">🐾</span>
            </div>
            <h1 className="text-2xl font-bold text-white">Jahnvar</h1>
            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center animate-wiggle">
              <span className="text-2xl">✨</span>
            </div>
          </Link>
        </div>
        <nav className="flex-1 px-3 py-6 space-y-2">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href || 
              (item.href !== '/dashboard' && location.pathname.startsWith(item.href));
            
            // Hide dashboard if no pets
            const petsData = pets?.data || [];
            if (item.href === '/app/dashboard' && petsData.length === 0) {
              return null;
            }
            
            return (
              <NavLink
                key={item.name}
                to={item.href}
                className={`group flex items-center px-4 py-3 text-sm font-bold rounded-xl transition-all duration-200 transform hover:scale-105 ${
                  isActive
                    ? 'bg-white/20 text-white shadow-lg backdrop-blur-sm'
                    : 'text-white/80 hover:bg-white/10 hover:text-white'
                }`}
              >
                <item.icon
                  className={`mr-4 flex-shrink-0 h-5 w-5 ${
                    isActive ? 'text-white animate-pulse' : 'text-white/60 group-hover:text-white'
                  }`}
                />
                {item.name}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-4 bg-white/10 backdrop-blur-sm">
          <div className="text-center text-white/80 text-xs">
            <span className="animate-bounce inline-block">🎉</span>
            <div className="mt-1">Happy Training!</div>
          </div>
        </div>
      </div>
    );
  }
};

export default Layout;